"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import MultiSelect from "@/components/ui/multi-select";
import SingleSelect from "@/components/ui/single-select";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { TagInput } from "@/components/ui/tag-input";
import { TinyMCEImagePicker } from "@/components/ui/tinymce-image-picker";
import { imageUploadPlugin, sanitizeEditorContent } from "@/components/ui/tinymce-upload-plugin";
import { JsonLdBuilder } from "@/components/ui/json-ld-builder";
import { SeoPreview } from "@/components/ui/seo-preview";
import { imageService } from "@/services/imageService";
import { articleService } from "@/services/articleService";
import { useArticleStore } from "@/stores/articleStore";
import { useExternalStore } from "@/stores/externalStore";
import { useCategoryStore } from "@/stores/categoryStore";
import { useAuthorStore } from "@/stores/authorStore";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { ArrowLeft, Save, X, Eye, EyeOff } from "lucide-react";
import { Editor } from "@tinymce/tinymce-react";
import { showToast } from "@/lib/toast";

import { SITE_DOMAINS } from "@/constants/site-domains";

const schema = z.object({
  title: z.string().max(255).optional(),
  content: z.string().optional(),
  slug: z.string().max(255).optional(),
  author: z.string().optional(),
  category: z.string().optional(),
  movie: z.string().optional(),
  actors: z.string().optional(),
  directors: z.string().optional(),
  pageTitle: z.string().optional(),
  pageDescription: z.string().optional(),
  canonicalUrl: z.string().optional(),
  keywords: z.array(z.string()).optional(),
  featuredImage: z.string().optional(),
  visibility: z.enum(["public", "private"]).optional(),
  domains: z.array(z.string()).optional(),
  publishDate: z.string().optional(),
  updateDate: z.string().optional(),
  mainKeyword: z.string().optional(),
  film_description: z.string().optional(),
  airingDays: z.array(z.string()).optional(),
  airingTime: z.string().optional(),
  year: z.number().optional(),
  origin_name: z.string().optional(),
  movieStatus: z.enum(["ongoing", "complete"]).optional(),
  movieType: z.enum(["single", "series", "cinema", "anime"]).optional(),
  country: z.string().optional(),
  // Image properties
  imageTitle: z.string().optional(),
  imageAlt: z.string().optional(),
  imageCaption: z.string().optional(),
  imageLinkImage: z.string().optional(),
  imageRel: z.string().optional(),
  imageTarget: z.string().optional(),
  schemaJsonLd: z.string().optional(),
  backgroundImage: z.string().optional(),
  current_episode: z.string().optional(),
  total_episode: z.string().optional(),
});

type FormDataType = z.infer<typeof schema>;

export default function EditArticlePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editorRef: any = useRef(null);

  const { fetchArticleById, selectedArticle, updateArticle } =
    useArticleStore();
  const {
    categories: externalCategories,
    movies,
    actors,
    directors,
    countries,
    fetchCategories: fetchExternalCategories,
    fetchMovies,
    fetchActors,
    fetchDirectors,
    fetchCountries,
  } = useExternalStore();
  const {
    categories: mainCategories,
    fetchCategories: fetchMainCategories,
  } = useCategoryStore();
  const { authors, fetchAuthors } = useAuthorStore();

  // Detect if article is from KKphim/OPhim (actors are strings, no directors/country)
  const [isKKPhim, setIsKKPhim] = useState(false);
  // Store KKphim actor/country names for display
  const [kkphimActorNames, setKkphimActorNames] = useState<string[]>([]);
  const [kkphimCountryNames, setKkphimCountryNames] = useState<string[]>([]);

  // Use main DB categories for KKphim, external DB for Ophim
  const categories = isKKPhim ? mainCategories : externalCategories;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);
  const [createdAt, setCreatedAt] = useState<string>("");
  const [updatedAt, setUpdatedAt] = useState<string>("");
  const [lastSaved, setLastSaved] = useState<string>("");
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSavedData, setLastSavedData] = useState<string>("");
  // Lưu title ban đầu (lúc load) để chỉ auto-generate slug khi user thực sự sửa title
  const initialTitleRef = useRef<string | null>(null);
  // Ẩn/hiện khỏi tin tức & tìm kiếm (isDisplay) - tách state riêng, không đi qua
  // selectedArticle của store để tránh cascade reset() làm mất nội dung đang sửa dở
  const [isDisplay, setIsDisplay] = useState(true);
  const [isTogglingDisplay, setIsTogglingDisplay] = useState(false);

  // Background image states
  const [backgroundImagePreview, setBackgroundImagePreview] =
    useState<string>("");
  const [isBackgroundDragging, setIsBackgroundDragging] = useState(false);
  const [isUploadingBackground, setIsUploadingBackground] = useState(false);

  const formatDateTime = (value?: string) => {
    if (!value) return "—";
    const d = new Date(value);
    const pad = (n: number) => String(n).padStart(2, "0");
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());
    const dd = pad(d.getDate());
    const MM = pad(d.getMonth() + 1);
    const yyyy = d.getFullYear();
    return `${hh}:${mm} ${dd}/${MM}/${yyyy}`;
  };

  const { register, handleSubmit, setValue, watch, reset } =
    useForm<FormDataType>({
      defaultValues: {
        title: "",
        content: "",
        slug: "",
        author: "",
        category: "",
        movie: "",
        actors: "",
        directors: "",
        pageTitle: "",
        pageDescription: "",
        canonicalUrl: "",
        keywords: [],
        featuredImage: "",
        mainKeyword: "",
        film_description: "",
        airingDays: [],
        airingTime: "",
        year: undefined,
        origin_name: "",
        movieStatus: undefined,
        movieType: undefined,
        country: "",
        visibility: "public",
        domains: ["phimmoihd.it.com"],
        publishDate: "",
        updateDate: "",
        // Image properties
        imageTitle: "",
        imageAlt: "",
        imageCaption: "",
        imageLinkImage: "",
        imageRel: "nofollow",
        imageTarget: "_self",
        schemaJsonLd: "",
        backgroundImage: "",
        current_episode: "",
        total_episode: "",
      },
    });

  useEffect(() => {
    fetchExternalCategories();
    fetchMainCategories({ page: 1, limit: 500 });
    fetchMovies();
    fetchActors();
    fetchDirectors();
    fetchCountries();
    fetchAuthors({ page: 1, limit: 1000 });
  }, [
    fetchExternalCategories,
    fetchMainCategories,
    fetchMovies,
    fetchActors,
    fetchDirectors,
    fetchCountries,
    fetchAuthors,
  ]);

  useEffect(() => {
    if (id) fetchArticleById(id);
  }, [id, fetchArticleById]);

  useEffect(() => {
    if (selectedArticle) {
      const raw: any = selectedArticle as any;
      const article = raw?.data?.data ?? raw; // unwrap theo API mẫu

      // Detect KKphim/OPhim article
      const isKK = !!(article.kkMovie || article.source === 'KKphim' || article.source === 'OPhim');
      setIsKKPhim(isKK);

      // KKphim: actors/country are strings, not ObjectIds
      if (isKK) {
        const actorNames = (article.actorsData || [])
          .map((a: any) => a?.name)
          .filter(Boolean);
        setKkphimActorNames(actorNames);

        const countryNames = (article.country || [])
          .map((c: any) => (typeof c === 'string' ? c : c?.name))
          .filter(Boolean);
        setKkphimCountryNames(countryNames);
      }

      const actorIds: string[] = isKK
        ? [] // KKphim: don't use ObjectId actors
        : (article.actors || [])
            .map((a: any) => (typeof a === "string" ? a : a?._id))
            .filter(Boolean);
      const directorIds: string[] = isKK
        ? [] // KKphim: no directors
        : (article.directors || [])
            .map((d: any) => (typeof d === "string" ? d : d?._id))
            .filter(Boolean);

      reset({
        title: article.title || "",
        content: article.content || "",
        slug: article.slug || "",
        author: article.author?._id || "",
        category: Array.isArray(article.category)
          ? article.category
              .map((c: any) => (typeof c === "string" ? c : c?._id))
              .filter(Boolean)
              .join(",")
          : article.category?._id || article.category || "",
        movie: article.movie?._id || article.movie || "",
        actors: actorIds.join(","),
        directors: directorIds.join(","),
        pageTitle: article.pageTitle || "",
        pageDescription: article.pageDescription || "",
        canonicalUrl: article.canonicalUrl || "",
        keywords: article.keywords || [],
        featuredImage: article.featuredImage?._id || "",
        mainKeyword: article.mainKeyword || "",
        film_description: article.film_description || "",
        airingDays: article.airingDays || [],
        airingTime: article.airingTime || "",
        year: article.year || undefined,
        origin_name: article.origin_name || "",
        movieStatus: (article as any).movieStatus || undefined,
        movieType: (article as any).movieType || undefined,
        country:
          (article as any).country?._id || (article as any).country || "",
        visibility: article.visibility || "public",
        domains: article.domains || ["phimmoihd.it.com"],
        publishDate: article.publishDate || "",
        updateDate: article.updatedAt || "",
        // Image properties
        imageTitle: article.featuredImage?.title || "",
        imageAlt: article.featuredImage?.alt || "",
        imageCaption: article.featuredImage?.caption || "",
        imageLinkImage: article.featuredImage?.linkImage || "",
        imageRel: article.featuredImage?.rel || "nofollow",
        imageTarget: article.featuredImage?.target || "_self",
        schemaJsonLd: (article as any).schemaJsonLd || "",
        backgroundImage: (article as any).backgroundImage || "",
        current_episode: (article as any).current_episode || "",
        total_episode: (article as any).total_episode || "",
      });
      if (article.featuredImage?.url)
        setPreviewImage(article.featuredImage.url);
      if ((article as any).backgroundImage)
        setBackgroundImagePreview((article as any).backgroundImage);
      setCreatedAt(article.createdAt || "");
      setUpdatedAt(article.updatedAt || "");
      setIsDisplay(article.isDisplay !== false);

      // Lưu title ban đầu để tránh auto-generate slug ghi đè slug đã có khi mới load form
      initialTitleRef.current = article.title || "";

      // Set initial saved data state
      const initialDataString = JSON.stringify({
        title: article.title || "",
        content: article.content || "",
        slug: article.slug || "",
        author: article.author?._id || "",
        category: Array.isArray(article.category)
          ? article.category
              .map((c: any) => (typeof c === "string" ? c : c?._id))
              .filter(Boolean)
              .join(",")
          : article.category?._id || article.category || "",
        movie: article.movie?._id || article.movie || "",
        actors: actorIds.join(","),
        directors: directorIds.join(","),
        pageTitle: article.pageTitle || "",
        pageDescription: article.pageDescription || "",
        canonicalUrl: article.canonicalUrl || "",
        keywords: article.keywords || [],
        visibility: article.visibility || "public",
        domains: article.domains || ["phimmoihd.it.com"],
        publishDate: article.publishDate || "",
        updateDate: article.updatedAt || "",
        mainKeyword: article.mainKeyword || "",
        film_description: article.film_description || "",
        airingDays: article.airingDays || [],
        airingTime: article.airingTime || "",
        year: article.year || undefined,
        origin_name: article.origin_name || "",
        movieStatus: (article as any).movieStatus || undefined,
        movieType: (article as any).movieType || undefined,
        country:
          (article as any).country?._id || (article as any).country || "",
        imageTitle: article.featuredImage?.title || "",
        imageAlt: article.featuredImage?.alt || "",
        imageCaption: article.featuredImage?.caption || "",
        imageLinkImage: article.featuredImage?.linkImage || "",
        imageRel: article.featuredImage?.rel || "nofollow",
        imageTarget: article.featuredImage?.target || "_self",
        schemaJsonLd: (article as any).schemaJsonLd || "",
        backgroundImage: (article as any).backgroundImage || "",
        current_episode: (article as any).current_episode || "",
        total_episode: (article as any).total_episode || "",
        hasNewFile: false,
      });
      setLastSavedData(initialDataString);
    }
  }, [selectedArticle, reset]);

  // Auto-generate slug từ title
  // Chỉ auto-generate khi user thực sự thay đổi title (không phải khi form vừa load data)
  const titleValue = watch("title");
  useEffect(() => {
    if (!titleValue || !titleValue.trim()) {
      return;
    }

    // Nếu title giống với giá trị ban đầu (khi load data) → không auto-generate,
    // tránh ghi đè slug đã có sẵn
    if (
      initialTitleRef.current !== null &&
      titleValue === initialTitleRef.current
    ) {
      return;
    }

    const slug = titleValue
      .toLowerCase()
      // Convert Vietnamese characters to Latin
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
      .replace(/đ/g, "d") // Convert đ to d
      .replace(/Đ/g, "d") // Convert Đ to d
      .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-") // Replace multiple hyphens with single
      .replace(/^-+|-+$/g, "") // Remove leading/trailing hyphens
      .trim();

    setValue("slug", slug);
  }, [titleValue, setValue]);

  const handleToggleDisplay = useCallback(async () => {
    if (!id || isTogglingDisplay) return;
    setIsTogglingDisplay(true);
    try {
      const response: any = await articleService.toggleDisplay(id);
      if (response.ok && response.data) {
        setIsDisplay(response.data.isDisplay !== false);
        showToast.success("Thay đổi hiển thị thành công");
      } else {
        showToast.error(response.data?.message || "Lỗi khi thay đổi hiển thị");
      }
    } catch (error: any) {
      showToast.error(error?.message || "Lỗi khi thay đổi hiển thị");
    } finally {
      setIsTogglingDisplay(false);
    }
  }, [id, isTogglingDisplay]);

  const handleOpenImagePicker = useCallback(
    () => setIsImagePickerOpen(true),
    []
  );
  const handleImageSelect = useCallback(
    (payload: {
      url: string;
      alt?: string;
      title?: string;
      caption?: string;
      linkImage?: string;
      width?: number;
      height?: number;
      rel?: string;
      target?: string;
      id?: string;
    }) => {
      // Chèn ảnh vào nội dung TinyMCE (không ảnh hưởng hình đại diện)
      if (editorRef.current) {
        const imgTag = `<img src="${payload.url}" ${
          payload.id ? `data-image-id="${payload.id}"` : ""
        } alt="${payload.alt || ""}" ${
          payload.width ? `width="${payload.width}"` : ""
        } ${payload.height ? `height="${payload.height}"` : ""} ${
          payload.title ? `title="${payload.title}"` : ""
        } style="max-width: 100%; height: auto;" />`;
        const html = payload.linkImage
          ? `<a href="${payload.linkImage}" ${
              payload.target ? `target="${payload.target}"` : ""
            } ${payload.rel ? `rel="${payload.rel}"` : ""}>${imgTag}</a>`
          : imgTag;
        editorRef.current.insertContent(html);
      }
      setIsImagePickerOpen(false);
    },
    []
  );

  // Auto-save function
  const autoSave = useCallback(async () => {
    if (!id) return;

    const formData = watch();

    // Create a string representation of current form data to compare
    const currentDataString = JSON.stringify({
      title: formData.title,
      content: formData.content,
      slug: formData.slug,
      author: formData.author,
      category: formData.category,
      movie: formData.movie,
      actors: formData.actors,
      directors: formData.directors,
      pageTitle: formData.pageTitle,
      pageDescription: formData.pageDescription,
      canonicalUrl: formData.canonicalUrl,
      keywords: formData.keywords,
      visibility: formData.visibility,
      domains: formData.domains,
      publishDate: formData.publishDate,
      updateDate: formData.updateDate,
      mainKeyword: formData.mainKeyword,
      film_description: formData.film_description,
      airingDays: formData.airingDays,
      airingTime: formData.airingTime,
      year: formData.year,
      origin_name: formData.origin_name,
      movieStatus: formData.movieStatus,
      movieType: formData.movieType,
      country: formData.country,
      imageTitle: formData.imageTitle,
      imageAlt: formData.imageAlt,
      imageCaption: formData.imageCaption,
      imageLinkImage: formData.imageLinkImage,
      imageRel: formData.imageRel,
      imageTarget: formData.imageTarget,
      schemaJsonLd: formData.schemaJsonLd,
      backgroundImage: formData.backgroundImage,
      current_episode: formData.current_episode,
      total_episode: formData.total_episode,
      hasNewFile: !!selectedFile,
    });

    // Only save if data has changed
    if (currentDataString === lastSavedData) {
      return; // No changes, skip save
    }

    setIsAutoSaving(true);

    try {
      const actorsArray = formData.actors
        ? formData.actors
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
      const directorsArray = formData.directors
        ? formData.directors
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
      const categoriesArray = formData.category
        ? formData.category
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];

      const saveData = new FormData();
      if (formData.title) saveData.append("title", formData.title);
      if (formData.content) saveData.append("content", formData.content);
      if (formData.slug) saveData.append("slug", formData.slug);
      if (formData.author) saveData.append("author", formData.author);
      categoriesArray.forEach((id) => saveData.append("category[]", id));
      if (formData.movie) saveData.append("movie", formData.movie);
      // KKphim: không gửi actors/directors (là string[], không phải ObjectId)
      if (!isKKPhim) {
        if (actorsArray.length === 0) {
          saveData.append("actors[]", "");
        } else {
          actorsArray.forEach((id) => saveData.append("actors[]", id));
        }
        if (directorsArray.length === 0) {
          saveData.append("directors[]", "");
        } else {
          directorsArray.forEach((id) => saveData.append("directors[]", id));
        }
      }
      if (formData.pageTitle) saveData.append("pageTitle", formData.pageTitle);
      if (formData.pageDescription)
        saveData.append("pageDescription", formData.pageDescription);
      if (formData.canonicalUrl)
        saveData.append("canonicalUrl", formData.canonicalUrl);
      (formData.keywords || []).forEach((kw) =>
        saveData.append("keywords[]", kw)
      );
      if (selectedFile) saveData.append("file", selectedFile);
      if (formData.visibility)
        saveData.append("visibility", formData.visibility);
      if (formData.publishDate)
        saveData.append("publishDate", formData.publishDate);
      if (formData.updateDate)
        saveData.append("updateDate", formData.updateDate);
      if (formData.mainKeyword)
        saveData.append("mainKeyword", formData.mainKeyword);
      if (formData.film_description !== undefined)
        saveData.append("film_description", formData.film_description || "");
      (formData.airingDays || []).forEach((day) =>
        saveData.append("airingDays[]", day)
      );
      // Nếu không chọn ngày nào, gửi khóa "airingDays" với JSON "[]" để backend hiểu là xóa toàn bộ
      // Không append chuỗi, để backend nhận thiếu key thay vì string
      (formData.domains || []).forEach((d) => saveData.append("domains[]", d));
      if (formData.airingTime)
        saveData.append("airingTime", formData.airingTime);
      if (formData.year !== undefined)
        saveData.append("year", String(formData.year));
      if (formData.origin_name !== undefined)
        saveData.append("origin_name", formData.origin_name);
      if ((formData as any).movieStatus)
        saveData.append("movieStatus", (formData as any).movieStatus as any);
      if ((formData as any).movieType)
        saveData.append("movieType", (formData as any).movieType as any);
      if (!isKKPhim && (formData as any).country)
        saveData.append("country", (formData as any).country as any);
      // Image properties
      if (formData.imageTitle !== undefined)
        saveData.append("imageTitle", formData.imageTitle || "");
      if (formData.imageAlt !== undefined)
        saveData.append("imageAlt", formData.imageAlt || "");
      if (formData.imageCaption !== undefined)
        saveData.append("imageCaption", formData.imageCaption || "");
      if (formData.imageLinkImage !== undefined)
        saveData.append("imageLinkImage", formData.imageLinkImage || "");
      if (formData.imageRel !== undefined)
        saveData.append("imageRel", formData.imageRel || "");
      if (formData.imageTarget !== undefined)
        saveData.append("imageTarget", formData.imageTarget || "");
      if (formData.schemaJsonLd !== undefined)
        saveData.append("schemaJsonLd", formData.schemaJsonLd || "");
      if (formData.backgroundImage !== undefined)
        saveData.append("backgroundImage", formData.backgroundImage || "");
      if (formData.current_episode !== undefined)
        saveData.append("current_episode", formData.current_episode || "");
      if (formData.total_episode !== undefined)
        saveData.append("total_episode", formData.total_episode || "");

      await updateArticle(id, saveData);
      setLastSaved(new Date().toLocaleTimeString());
      setLastSavedData(currentDataString); // Update the saved data state
    } catch (error) {
      console.error("Auto-save failed:", error);
    } finally {
      setIsAutoSaving(false);
    }
  }, [id, watch, selectedFile, updateArticle, lastSavedData, isKKPhim]);

  // Auto-save every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      autoSave();
    }, 15000);

    return () => clearInterval(interval);
  }, [autoSave]);

  const onSubmit = async (data: FormDataType) => {
    if (!id) return;
    setIsSubmitting(true);
    try {
      const actorsArray = data.actors
        ? data.actors
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
      const directorsArray = data.directors
        ? data.directors
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
      const categoriesArray = data.category
        ? data.category
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];

      const sanitizedContent = data.content
        ? await sanitizeEditorContent(data.content)
        : data.content;

      const formData = new FormData();
      if (data.title) formData.append("title", data.title);
      if (sanitizedContent) formData.append("content", sanitizedContent);
      if (data.slug) formData.append("slug", data.slug);
      if (data.author) formData.append("author", data.author);
      categoriesArray.forEach((id) => formData.append("category[]", id));
      if (data.movie) formData.append("movie", data.movie);
      // KKphim: không gửi actors/directors (là string[], không phải ObjectId)
      if (!isKKPhim) {
        if (actorsArray.length === 0) {
          formData.append("actors[]", "");
        } else {
          actorsArray.forEach((id) => formData.append("actors[]", id));
        }
        if (directorsArray.length === 0) {
          formData.append("directors[]", "");
        } else {
          directorsArray.forEach((id) => formData.append("directors[]", id));
        }
      }
      if (data.pageTitle) formData.append("pageTitle", data.pageTitle);
      if (data.pageDescription)
        formData.append("pageDescription", data.pageDescription);
      if (data.canonicalUrl) formData.append("canonicalUrl", data.canonicalUrl);
      (data.keywords || []).forEach((kw) => formData.append("keywords[]", kw));
      if (selectedFile) formData.append("file", selectedFile);
      if (data.visibility) formData.append("visibility", data.visibility);
      if (data.publishDate) formData.append("publishDate", data.publishDate);
      if (data.updateDate) formData.append("updateDate", data.updateDate);
      if (data.mainKeyword) formData.append("mainKeyword", data.mainKeyword);
      if (data.film_description !== undefined)
        formData.append("film_description", data.film_description || "");
      (data.airingDays || []).forEach((day) =>
        formData.append("airingDays[]", day)
      );
      // Nếu không chọn ngày nào, gửi khóa "airingDays" với JSON "[]" để backend hiểu là xóa toàn bộ
      // Không append chuỗi, để backend nhận thiếu key thay vì string
      (data.domains || []).forEach((d) => formData.append("domains[]", d));
      if (data.airingTime) formData.append("airingTime", data.airingTime);
      if (data.year !== undefined) formData.append("year", String(data.year));
      if (data.origin_name !== undefined)
        formData.append("origin_name", data.origin_name);

      if ((data as any).movieStatus)
        formData.append("movieStatus", (data as any).movieStatus as any);
      if ((data as any).movieType)
        formData.append("movieType", (data as any).movieType as any);
      if (!isKKPhim && (data as any).country)
        formData.append("country", (data as any).country as any);
      // Image properties - luôn gửi nếu có giá trị (kể cả rỗng)
      if (data.imageTitle !== undefined)
        formData.append("imageTitle", data.imageTitle || "");
      if (data.imageAlt !== undefined)
        formData.append("imageAlt", data.imageAlt || "");
      if (data.imageCaption !== undefined)
        formData.append("imageCaption", data.imageCaption || "");
      if (data.imageLinkImage !== undefined)
        formData.append("imageLinkImage", data.imageLinkImage || "");
      if (data.imageRel !== undefined)
        formData.append("imageRel", data.imageRel || "");
      if (data.imageTarget !== undefined)
        formData.append("imageTarget", data.imageTarget || "");
      if (data.schemaJsonLd !== undefined)
        formData.append("schemaJsonLd", data.schemaJsonLd || "");
      if (data.backgroundImage !== undefined)
        formData.append("backgroundImage", data.backgroundImage || "");
      if (data.current_episode !== undefined)
        formData.append("current_episode", data.current_episode || "");
      if (data.total_episode !== undefined)
        formData.append("total_episode", data.total_episode || "");

      await updateArticle(id, formData);

      // Update saved data state after manual save
      const currentDataString = JSON.stringify({
        title: data.title,
        content: data.content,
        slug: data.slug,
        author: data.author,
        category: data.category,
        movie: data.movie,
        actors: data.actors,
        directors: data.directors,
        pageTitle: data.pageTitle,
        pageDescription: data.pageDescription,
        canonicalUrl: data.canonicalUrl,
        keywords: data.keywords,
        visibility: data.visibility,
        domains: data.domains,
        publishDate: data.publishDate,
        updateDate: data.updateDate,
        mainKeyword: data.mainKeyword,
        film_description: data.film_description,
        airingDays: data.airingDays,
        airingTime: data.airingTime,
        year: data.year,
        origin_name: data.origin_name,
        movieStatus: (data as any).movieStatus,
        movieType: (data as any).movieType,
        country: (data as any).country,
        imageTitle: data.imageTitle,
        imageAlt: data.imageAlt,
        imageCaption: data.imageCaption,
        imageLinkImage: data.imageLinkImage,
        imageRel: data.imageRel,
        imageTarget: data.imageTarget,
        schemaJsonLd: data.schemaJsonLd,
        backgroundImage: data.backgroundImage,
        current_episode: data.current_episode,
        total_episode: data.total_episode,
        hasNewFile: !!selectedFile,
      });
      setLastSavedData(currentDataString);

      // navigate("/articles");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Drag & drop handlers for featured image
  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith("image/")) {
      alert("Vui lòng chọn file hình ảnh");
      return;
    }
    setSelectedFile(file);
    setPreviewImage(URL.createObjectURL(file));
    setValue("featuredImage", file.name);

    // Cập nhật các thuộc tính hình ảnh khi upload file mới
    const baseName = file.name.replace(/\.[^/.]+$/, "");
    setValue("imageTitle", baseName);
    setValue("imageAlt", baseName);
    setValue("imageCaption", "");
    setValue("imageLinkImage", "");
    setValue("imageRel", "nofollow");
    setValue("imageTarget", "_self");
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files[0]) handleFileSelect(files[0]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  // Background image handlers
  const handleBackgroundFileSelect = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      alert("Vui lòng chọn file hình ảnh");
      return;
    }

    setBackgroundImagePreview(URL.createObjectURL(file));
    setIsUploadingBackground(true);

    try {
      const response = await imageService.upload(file, {
        alt: file.name.replace(/\.[^/.]+$/, ""),
        title: file.name.replace(/\.[^/.]+$/, ""),
      });

      // Handle nested response structure like tinymce-upload-plugin
      const imageData =
        (response as any)?.data?.data?.data ||
        (response as any)?.data?.data ||
        (response as any)?.data;
      const imageUrl = imageData?.url;

      if (imageUrl) {
        setValue("backgroundImage", imageUrl);
        setBackgroundImagePreview(imageUrl);
      } else {
        throw new Error("No image URL in response");
      }
    } catch (error) {
      console.error("Background image upload failed:", error);
      alert("Không thể tải lên hình nền");
      setBackgroundImagePreview("");
    } finally {
      setIsUploadingBackground(false);
    }
  };

  const handleBackgroundDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsBackgroundDragging(false);
    const files = e.dataTransfer.files;
    if (files && files[0]) handleBackgroundFileSelect(files[0]);
  };

  const handleBackgroundDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsBackgroundDragging(true);
  };

  const handleBackgroundDragLeave = () => setIsBackgroundDragging(false);

  const clearBackgroundImage = () => {
    setBackgroundImagePreview("");
    setValue("backgroundImage", "");
  };

  const contentValue = watch("content");
  const memoEditor = useMemo(
    () => (
      <Editor
        tinymceScriptSrc="/tinymce/tinymce.min.js"
        licenseKey="gpl"
        onInit={(_evt, editor) => (editorRef.current = editor)}
        value={contentValue || ""}
        onEditorChange={(content) => setValue("content", content)}
        init={{
          height: 400,
          menubar: false,
          plugins: [
            "code",
            "advlist",
            "anchor",
            "autolink",
            "charmap",
            "fullscreen",
            "help",
            "image",
            "insertdatetime",
            "link",
            "lists",
            "media",
            "preview",
            "searchreplace",
            "table",
            "visualblocks",
          ],
          toolbar:
            "code | undo redo | styles | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link | customimage",
          content_style:
            "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
          link_rel_list: [
            { title: "Nothing", value: "" },
            {
              title: "No Referrer",
              value: "noreferrer",
            },
            { title: "No Follow", value: "nofollow" },
            { title: "Sponsored", value: "sponsored" },
            { title: "UGC", value: "ugc" },
          ],
          paste_data_images: true,
          automatic_uploads: true,
          file_picker_types: "image",
          setup: (editor) => {
            editor.on("init", () => {
              editor.ui.registry.addButton("customimage", {
                text: "Hình ảnh",
                icon: "image",
                onAction: () => handleOpenImagePicker(),
              });
              imageUploadPlugin(editor);
            });
          },
        }}
      />
    ),
    [contentValue, setValue, handleOpenImagePicker]
  );

  const isMovieComplete = watch("movieStatus") === "complete";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/articles")}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" /> Quay lại
            </Button>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Chỉnh sửa phim
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Auto-save status */}
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              {isAutoSaving ? (
                <div className="flex items-center gap-1">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                  <span>Đang tự động lưu...</span>
                </div>
              ) : lastSaved ? (
                <span>Đã lưu lúc {lastSaved}</span>
              ) : null}
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleToggleDisplay}
              disabled={isTogglingDisplay}
              className="flex items-center gap-2"
              title={
                isDisplay
                  ? "Ẩn khỏi tin tức & tìm kiếm (vẫn xem được qua URL)"
                  : "Hiện lại trên tin tức & tìm kiếm"
              }
            >
              {isDisplay ? (
                <Eye className="h-4 w-4" />
              ) : (
                <EyeOff className="h-4 w-4 text-orange-500" />
              )}
              {isDisplay ? "Đang hiện" : "Đang ẩn"}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/articles")}
              className="flex items-center gap-2"
            >
              <X className="h-4 w-4" /> Hủy
            </Button>
            <Button
              type="submit"
              form="edit-article-form"
              disabled={isSubmitting}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            >
              <Save className="h-4 w-4" />{" "}
              {isSubmitting ? "Đang lưu..." : "Lưu thay đổi"}
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-12 p-6">
        <form
          id="edit-article-form"
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <Label className="text-lg font-semibold mb-3 block">
                  Nội dung
                </Label>
                {memoEditor}
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <Label className="text-lg font-semibold mb-3 block">
                  Mô tả phim
                </Label>
                <Textarea
                  {...register("film_description")}
                  placeholder="Nhập mô tả phim..."
                  rows={4}
                  className="w-full"
                />
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">Thông tin phim</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="author">Tên tác giả</Label>
                    <MultiSelect
                      options={authors.map((a) => ({
                        label: a.name,
                        value: a._id,
                      }))}
                      value={(watch("author") || "")
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean)}
                      onChange={(vals) => setValue("author", vals.join(", "))}
                      placeholder="Người viết"
                    />
                  </div>

                  <div>
                    <Label htmlFor="category">Thể loại phim</Label>
                    <MultiSelect
                      options={categories.map((c) => ({
                        label: c.name,
                        value: c._id,
                      }))}
                      value={(watch("category") || "")
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean)}
                      onChange={(vals) => setValue("category", vals.join(", "))}
                      placeholder="Chọn thể loại"
                    />
                  </div>

                  <div>
                    <Label htmlFor="movie">Phim</Label>
                    <SingleSelect
                      options={movies.map((movie) => ({
                        label: movie.name,
                        value: movie._id,
                      }))}
                      placeholder="Chọn phim"
                      value={watch("movie") || ""}
                      onChange={() => {}} // Disable by not allowing changes
                    />
                  </div>

                  <div>
                    <Label htmlFor="airingDays">Ngày chiếu phim</Label>
                    <MultiSelect
                      options={[
                        { label: "Thứ 2", value: "Thứ 2" },
                        { label: "Thứ 3", value: "Thứ 3" },
                        { label: "Thứ 4", value: "Thứ 4" },
                        { label: "Thứ 5", value: "Thứ 5" },
                        { label: "Thứ 6", value: "Thứ 6" },
                        { label: "Thứ 7", value: "Thứ 7" },
                        { label: "Chủ nhật", value: "Chủ nhật" },
                      ]}
                      placeholder="Chọn ngày chiếu"
                      value={watch("airingDays") || []}
                      onChange={(values) => {
                        if (isMovieComplete) return;
                        setValue("airingDays", values);
                      }}
                      className={
                        isMovieComplete
                          ? "pointer-events-none opacity-50"
                          : undefined
                      }
                    />
                  </div>

                  <div>
                    <Label htmlFor="airingTime">Giờ chiếu phim</Label>
                    <Input
                      id="airingTime"
                      type="time"
                      {...register("airingTime")}
                      className={
                        isMovieComplete
                          ? "pointer-events-none opacity-50"
                          : undefined
                      }
                      disabled={isMovieComplete}
                    />
                  </div>

                  <div>
                    <Label htmlFor="year">Năm phát hành</Label>
                    <Input
                      id="year"
                      type="number"
                      {...register("year", { valueAsNumber: true })}
                      placeholder="VD: 2024"
                    />
                  </div>

                  <div>
                    <Label htmlFor="current_episode">Tập hiện tại</Label>
                    <Input
                      id="current_episode"
                      {...register("current_episode")}
                      placeholder="VD: 10"
                    />
                  </div>

                  <div>
                    <Label htmlFor="total_episode">Tổng số tập</Label>
                    <Input
                      id="total_episode"
                      {...register("total_episode")}
                      placeholder="VD: 24"
                    />
                  </div>

                  <div>
                    <Label htmlFor="origin_name">Tên gốc phim</Label>
                    <Input
                      id="origin_name"
                      {...register("origin_name")}
                      placeholder="Nhập tên gốc phim..."
                    />
                  </div>

                  <div>
                    <Label htmlFor="movieStatus">Trạng thái phim</Label>
                    <Select
                      value={watch("movieStatus") || ""}
                      onValueChange={(v) => {
                        setValue("movieStatus", v as any);
                        if (v === "complete") {
                          setValue("airingDays", []);
                          setValue("airingTime", "");
                        }
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Chọn trạng thái" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ongoing">Đang chiếu</SelectItem>
                        <SelectItem value="complete">Hoàn thành</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="movieType">Loại phim</Label>
                    <Select
                      value={watch("movieType") || ""}
                      onValueChange={(v) => setValue("movieType", v as any)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Chọn loại phim" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">Phim lẻ</SelectItem>
                        <SelectItem value="series">Phim bộ</SelectItem>
                        <SelectItem value="cinema">Phim chiếu rạp</SelectItem>
                        <SelectItem value="anime">Hoạt hình/Anime</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="country">Quốc gia</Label>
                    {isKKPhim ? (
                      <div className="flex flex-wrap gap-1 min-h-[38px] rounded-md border border-input bg-gray-50 dark:bg-gray-900 px-3 py-2">
                        {kkphimCountryNames.length > 0 ? (
                          kkphimCountryNames.map((name, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800"
                            >
                              {name}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-gray-400">Không có quốc gia</span>
                        )}
                      </div>
                    ) : (
                      <Select
                        value={watch("country") || ""}
                        onValueChange={(v) => setValue("country", v as any)}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Chọn quốc gia" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem key={country._id} value={country._id}>
                              {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="actors">Diễn viên</Label>
                    {isKKPhim ? (
                      <div className="flex flex-wrap gap-1 min-h-[38px] rounded-md border border-input bg-gray-50 dark:bg-gray-900 px-3 py-2">
                        {kkphimActorNames.length > 0 ? (
                          kkphimActorNames.map((name, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800"
                            >
                              {name}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-gray-400">Không có diễn viên</span>
                        )}
                      </div>
                    ) : (
                      <MultiSelect
                        options={actors.map((actor) => ({
                          label: actor.name,
                          value: actor._id,
                        }))}
                        placeholder="Chọn diễn viên"
                        value={
                          watch("actors")
                            ? watch("actors")!.split(",").filter(Boolean)
                            : []
                        }
                        onChange={(values) =>
                          setValue("actors", values.join(","))
                        }
                      />
                    )}
                  </div>

                  <div>
                    <Label htmlFor="directors">Đạo diễn</Label>
                    {isKKPhim ? (
                      <div className="flex flex-wrap gap-1 min-h-[38px] rounded-md border border-input bg-gray-50 dark:bg-gray-900 px-3 py-2">
                        <span className="text-sm text-gray-400">Nguồn KKphim/OPhim không có dữ liệu đạo diễn</span>
                      </div>
                    ) : (
                      <MultiSelect
                        options={directors.map((director) => ({
                          label: director.name,
                          value: director._id,
                        }))}
                        placeholder="Chọn đạo diễn"
                        value={
                          watch("directors")
                            ? watch("directors")!.split(",").filter(Boolean)
                            : []
                        }
                        onChange={(values) =>
                          setValue("directors", values.join(","))
                        }
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="title">Tiêu đề (Thẻ H1)</Label>
                    <Input
                      id="title"
                      {...register("title")}
                      placeholder="Phim..."
                    />
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <Label htmlFor="pageTitle">Tiêu đề trang</Label>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {watch("pageTitle")?.length || 0} / 70 ký tự
                      </span>
                    </div>
                    <Input
                      id="pageTitle"
                      {...register("pageTitle")}
                      maxLength={70}
                    />
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <Label htmlFor="pageDescription">Mô tả trang</Label>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {watch("pageDescription")?.length || 0} / 320 ký tự
                      </span>
                    </div>
                    <Textarea
                      id="pageDescription"
                      {...register("pageDescription")}
                      rows={3}
                      maxLength={320}
                    />
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="mainKeyword">Keyword chính</Label>
                      <Input
                        id="mainKeyword"
                        {...register("mainKeyword")}
                        placeholder="Nhập keyword chính..."
                      />
                    </div>

                    <div>
                      <Label htmlFor="slug">Đường dẫn</Label>
                      <Input id="slug" {...register("slug")} />
                    </div>

                    <div>
                      <Label htmlFor="canonicalUrl">Canonical URL</Label>
                      <Input
                        id="canonicalUrl"
                        {...register("canonicalUrl")}
                        maxLength={255}
                      />
                    </div>

                    <div>
                      <Label htmlFor="keywords">Keywords</Label>
                      <TagInput
                        value={watch("keywords") || []}
                        onChange={(tags) => setValue("keywords", tags)}
                        maxTags={10}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {/* SEO Preview */}
              <SeoPreview
                pageTitle={watch("pageTitle") || watch("title") || ""}
                pageDescription={watch("pageDescription") || ""}
                url={
                  watch("canonicalUrl") ||
                  `https://phimmoihd.it.com/draft/phim/${watch("slug") || ""}`
                }
                siteName="GhienPhim"
                previewUrl={
                  watch("slug")
                    ? `https://phimmoihd.it.com/draft/phim/${watch("slug")}`
                    : undefined
                }
              />

              <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
                <h3 className="font-semibold mb-4">Hiển thị & Ngày tháng</h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="visibility">Chế độ hiển thị</Label>
                    <Select
                      value={watch("visibility") || "public"}
                      onValueChange={(v) => setValue("visibility", v as any)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Chọn chế độ" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Công khai</SelectItem>
                        <SelectItem value="private">Riêng tư</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="domains">Hiện trên site</Label>
                    <MultiSelect
                      options={SITE_DOMAINS}
                      placeholder="Chọn site hiển thị"
                      value={watch("domains") || []}
                      onChange={(values) => setValue("domains", values)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Bỏ trống = ẩn bài khỏi mọi site. Chỉ áp dụng khi ở chế độ
                      Công khai — bài Riêng tư luôn ẩn bất kể chọn gì ở đây.
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="publishDate">Ngày xuất bản</Label>
                    <DateTimePicker
                      date={
                        watch("publishDate")
                          ? new Date(watch("publishDate")!)
                          : undefined
                      }
                      onChange={(date) =>
                        setValue("publishDate", date?.toISOString() || "")
                      }
                      placeholder="Chọn ngày xuất bản"
                    />
                  </div>

                  <div>
                    <Label htmlFor="updateDate">Ngày cập nhật</Label>
                    <DateTimePicker
                      date={
                        watch("updateDate")
                          ? new Date(watch("updateDate")!)
                          : undefined
                      }
                      onChange={(date) =>
                        setValue("updateDate", date?.toISOString() || "")
                      }
                      placeholder="Chọn ngày cập nhật"
                    />
                  </div>

                  <div className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
                    <p>
                      <strong>Ngày tạo:</strong> {formatDateTime(createdAt)}
                    </p>
                    <p>
                      <strong>Chỉnh sửa lần cuối:</strong>{" "}
                      {formatDateTime(updatedAt)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Hình đại diện
                    </h3>
                  </div>

                  {/* Image Properties */}
                  <div className="space-y-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="imageTitle">Tiêu đề hình ảnh</Label>
                        <Input
                          id="imageTitle"
                          {...register("imageTitle")}
                          placeholder="Tiêu đề hình ảnh"
                        />
                      </div>
                      <div>
                        <Label htmlFor="imageAlt">Alt text</Label>
                        <Input
                          id="imageAlt"
                          {...register("imageAlt")}
                          placeholder="Mô tả hình ảnh"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="imageCaption">Caption</Label>
                      <Textarea
                        id="imageCaption"
                        {...register("imageCaption")}
                        placeholder="Chú thích hình ảnh"
                        rows={2}
                      />
                    </div>

                    {/* Hidden fields for imageLinkImage, imageRel, imageTarget */}
                    <input type="hidden" {...register("imageLinkImage")} />
                    <input type="hidden" {...register("imageRel")} />
                    <input type="hidden" {...register("imageTarget")} />
                  </div>
                  <div
                    ref={dropRef}
                    className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 cursor-pointer group min-h-56 flex flex-col items-center justify-center ${
                      isDragging
                        ? "border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 scale-[1.02] shadow-lg"
                        : "border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:bg-gradient-to-br hover:from-gray-50 hover:to-blue-50 dark:hover:from-gray-800 dark:hover:to-blue-950 hover:shadow-md"
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() =>
                      document
                        .getElementById("edit-featured-image-input")
                        ?.click()
                    }
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleFileSelect(f);
                      }}
                      className="hidden"
                      id="edit-featured-image-input"
                    />

                    {previewImage ? (
                      <div className="flex flex-col items-center gap-4">
                        <img
                          src={previewImage}
                          alt="Preview"
                          className="w-60 h-40 rounded-xl object-cover shadow-lg border"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewImage("");
                            setSelectedFile(null);
                            setValue("featuredImage", "");
                            // Reset các thuộc tính hình ảnh khi xóa
                            setValue("imageTitle", "");
                            setValue("imageAlt", "");
                            setValue("imageCaption", "");
                            setValue("imageLinkImage", "");
                            setValue("imageRel", "nofollow");
                            setValue("imageTarget", "_self");
                          }}
                          className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100 hover:border-red-300"
                        >
                          Xóa ảnh
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center group-hover:from-blue-200 group-hover:to-indigo-200 transition-all duration-300">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-8 h-8 text-blue-600 group-hover:text-blue-700 transition-colors duration-300"
                          >
                            <path d="M2.25 6.75A2.25 2.25 0 014.5 4.5h15a2.25 2.25 0 012.25 2.25v10.5A2.25 2.25 0 0119.5 19.5h-15A2.25 2.25 0 013 17.25v-.75zM6 9.75a1.5 1.5 0 103 0 1.5 1.5 0 00-3 0z" />
                            <path d="M3 16.5l4.94-4.94a1.5 1.5 0 012.12 0L15 16.5l2.47-2.47a1.5 1.5 0 012.12 0L21 15.34V17.25A2.25 2.25 0 0118.75 19.5h-13.5A2.25 2.25 0 013 17.25v-.75z" />
                          </svg>
                        </div>
                        <div className="text-center">
                          <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                            Thêm hình đại diện
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Kéo thả hình ảnh vào đây hoặc nhấp để chọn file
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Hỗ trợ: JPG, PNG, GIF (tối đa 10MB)
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            document
                              .getElementById("edit-featured-image-input")
                              ?.click();
                          }}
                          className="bg-white dark:bg-gray-800 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950 hover:border-blue-300 hover:text-blue-800 transition-all duration-200"
                        >
                          Chọn file
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Background Image Section */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Hình nền (Background)
                    </h3>
                  </div>

                  <div
                    className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 cursor-pointer group min-h-40 flex flex-col items-center justify-center ${
                      isBackgroundDragging
                        ? "border-purple-500 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950 dark:to-indigo-950 scale-[1.02] shadow-lg"
                        : "border-gray-300 dark:border-gray-600 hover:border-purple-400 hover:bg-gradient-to-br hover:from-gray-50 hover:to-purple-50 dark:hover:from-gray-800 dark:hover:to-purple-950 hover:shadow-md"
                    }`}
                    onDragOver={handleBackgroundDragOver}
                    onDragLeave={handleBackgroundDragLeave}
                    onDrop={handleBackgroundDrop}
                    onClick={() =>
                      document
                        .getElementById("edit-background-image-input")
                        ?.click()
                    }
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleBackgroundFileSelect(f);
                      }}
                      className="hidden"
                      id="edit-background-image-input"
                    />

                    {isUploadingBackground ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Đang tải lên...
                        </span>
                      </div>
                    ) : backgroundImagePreview ? (
                      <div className="flex flex-col items-center gap-4">
                        <img
                          src={backgroundImagePreview}
                          alt="Background Preview"
                          className="w-full max-w-md h-32 rounded-xl object-cover shadow-lg border"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            clearBackgroundImage();
                          }}
                          className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100 hover:border-red-300"
                        >
                          Xóa hình nền
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-xl flex items-center justify-center group-hover:from-purple-200 group-hover:to-indigo-200 transition-all duration-300">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-6 h-6 text-purple-600 group-hover:text-purple-700 transition-colors duration-300"
                          >
                            <path
                              fillRule="evenodd"
                              d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="text-center">
                          <h4 className="text-base font-medium text-gray-900 dark:text-white">
                            Thêm hình nền
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Kéo thả hình ảnh vào đây hoặc nhấp để chọn file
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* JSON-LD Schema Section */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Schema JSON-LD
                    </h3>
                    <JsonLdBuilder
                      value={watch("schemaJsonLd") || ""}
                      onChange={(value) => setValue("schemaJsonLd", value)}
                      formData={{
                        title: watch("title") || "",
                        pageTitle: watch("pageTitle") || "",
                        pageDescription: watch("pageDescription") || "",
                        slug: watch("slug") || "",
                        canonicalUrl: watch("canonicalUrl") || "",
                        mainKeyword: watch("mainKeyword") || "",
                        keywords: watch("keywords") || [],
                        publishDate: watch("publishDate") || "",
                        updateDate: watch("updateDate") || "",
                        year: watch("year"),
                        origin_name: watch("origin_name") || "",
                        film_description: watch("film_description") || "",
                        actors: watch("actors") || "",
                        directors: watch("directors") || "",
                      }}
                      availableFields={[
                        { key: "title", label: "Tiêu đề (H1)" },
                        { key: "pageTitle", label: "Tiêu đề trang" },
                        { key: "pageDescription", label: "Mô tả trang" },
                        { key: "slug", label: "Đường dẫn (slug)" },
                        { key: "canonicalUrl", label: "Canonical URL" },
                        { key: "mainKeyword", label: "Keyword chính" },
                        { key: "keywords", label: "Keywords" },
                        { key: "publishDate", label: "Ngày xuất bản" },
                        { key: "updateDate", label: "Ngày cập nhật" },
                        { key: "year", label: "Năm phát hành" },
                        { key: "origin_name", label: "Tên gốc phim" },
                        { key: "film_description", label: "Mô tả phim" },
                        {
                          key: "directorName",
                          label: "Đạo diễn",
                          getValue: (data) => {
                            if (isKKPhim) return "";
                            const directorIds = data.directors
                              ? data.directors.split(",").filter(Boolean)
                              : [];
                            const directorNames = directorIds
                              .map(
                                (id: string) =>
                                  directors.find((d) => d._id === id)?.name
                              )
                              .filter(Boolean);
                            return directorNames.join(", ");
                          },
                        },
                        {
                          key: "actorNames",
                          label: "Diễn viên",
                          getValue: () => {
                            if (isKKPhim) return kkphimActorNames.join(", ");
                            const actorIds = watch("actors")
                              ? watch("actors")!.split(",").filter(Boolean)
                              : [];
                            const actorNames = actorIds
                              .map(
                                (id: string) =>
                                  actors.find((a) => a._id === id)?.name
                              )
                              .filter(Boolean);
                            return actorNames.join(", ");
                          },
                        },
                        {
                          key: "actorsJsonLd",
                          label: "Diễn viên (JSON-LD format)",
                          getValue: () => {
                            if (isKKPhim) {
                              const actorObjects = kkphimActorNames.map((name) => ({
                                "@type": "Person",
                                name,
                              }));
                              return JSON.stringify(actorObjects);
                            }
                            const actorIds = watch("actors")
                              ? watch("actors")!.split(",").filter(Boolean)
                              : [];
                            const actorObjects = actorIds
                              .map((id: string) => {
                                const actor = actors.find((a) => a._id === id);
                                return actor
                                  ? { "@type": "Person", name: actor.name }
                                  : null;
                              })
                              .filter(Boolean);
                            return JSON.stringify(actorObjects);
                          },
                        },
                        {
                          key: "directorsJsonLd",
                          label: "Đạo diễn (JSON-LD format)",
                          getValue: (data) => {
                            if (isKKPhim) return "[]";
                            const directorIds = data.directors
                              ? data.directors.split(",").filter(Boolean)
                              : [];
                            const directorObjects = directorIds
                              .map((id: string) => {
                                const director = directors.find(
                                  (d) => d._id === id
                                );
                                return director
                                  ? { "@type": "Person", name: director.name }
                                  : null;
                              })
                              .filter(Boolean);
                            return JSON.stringify(directorObjects);
                          },
                        },
                      ]}
                      pageType="article"
                    />
                  </div>
                  <Textarea
                    id="schemaJsonLd"
                    {...register("schemaJsonLd")}
                    placeholder='{"@context":"https://schema.org","@type":"Movie",...}'
                    rows={8}
                    className="font-mono text-sm"
                  />
                  {watch("schemaJsonLd") &&
                    (() => {
                      try {
                        JSON.parse(watch("schemaJsonLd")!);
                        return (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ JSON hợp lệ
                          </p>
                        );
                      } catch {
                        return (
                          <p className="text-xs text-red-600 mt-1">
                            ✗ JSON không hợp lệ
                          </p>
                        );
                      }
                    })()}
                </div>
              </div>
            </div>
          </div>
        </form>

        <TinyMCEImagePicker
          isOpen={isImagePickerOpen}
          onClose={() => setIsImagePickerOpen(false)}
          onSelect={handleImageSelect}
        />
      </div>
    </div>
  );
}
